import { useAuth } from "@clerk/clerk-react";
import axios from "axios";
import { IBackendMenuMigrationResponse } from "../../types/backend";
import {
  convertAgentBlueprintToBackend,
  convertAgentBlueprintToFrontend,
} from "../converters/agent-blueprint-converter";
import {
  convertMenuConfigToBackend,
  convertMenuToFrontend,
} from "../converters/menu-converter";
import { IMenuModuleConfig, Menu } from "../../types/menu";
import { IAgent, IAgentBlueprint } from "../../types/base";
import { ISpotOnMenu, IToastConfig } from "../../types/pos";
import { IOrganization } from "@/components/organization-picker";
import { StripeCustomerResponse } from "@/components/stripe-customer-card";
import {
  createApiClient,
  createMenuClient,
  getErrorMessage,
} from "./apiService";
import { StripeCustomerPost } from "@/components/go-live-dialog";
import { StripePaymentMethod } from "@loman/utils";
import { AgentCall } from "@/types/call";
export type {
  CreateAgentEvalRequest,
  AgentEvalResponse,
  EvalTranscriptItem,
  EvalRunResponse,
  EvalFilter,
  EvalWithRunsResponse,
  EvalsStatsResponse,
  PaginatedEvalRunsResponse,
} from "./types/eval";
import type {
  CreateAgentEvalRequest,
  AgentEvalResponse,
  EvalRunResponse,
  EvalWithRunsResponse,
  EvalsStatsResponse,
  PaginatedEvalRunsResponse,
  FetchEvalsPageOptions,
  FetchEvalRunsPageOptions,
  UpdateAgentEvalRequest,
} from "./types/eval";
import { AgentOverviewMetricsResponse, DateRange } from "./types/overview";
import {
  PhoneNumberLookupResponse,
  PhoneNumberPurchaseRequest,
  PhoneNumberPurchaseResponse,
} from "./types/phone-number";
export type {
  PhoneNumberErrorResponse,
  PhoneNumberLookupResponse,
  PhoneNumberPurchaseRequest,
  PhoneNumberPurchaseResponse,
} from "./types/phone-number";

export function useAgentEditorApi() {
  const { getToken } = useAuth();
  const apiClient = createApiClient(getToken);
  const menuClient = createMenuClient();

  // TODO: buy the number on agent create
  const createAgent = async (agent: IAgentBlueprint | null) => {
    if (!agent) {
      throw new Error("Agent is required");
    }
    const agentToSubmit = convertAgentBlueprintToBackend(agent);
    if (!agentToSubmit) {
      throw new Error("Failed to convert agent blueprint to backend");
    }
    agentToSubmit.phoneNumber = undefined;
    agentToSubmit.slug = undefined;
    agentToSubmit.hoursLoman = undefined;
    agentToSubmit.faqs = undefined;
    agentToSubmit.productId = undefined;
    agentToSubmit.subscriptionItemId = undefined;
    agentToSubmit.createdAt = undefined;
    agentToSubmit.updatedAt = undefined;
    agentToSubmit.deletedAt = undefined;
    agentToSubmit.pausedUntil = undefined;
    agentToSubmit.allowAnon = undefined;
    agentToSubmit.panic = undefined;
    agentToSubmit.reservationExternalId = undefined;

    const response = await apiClient.post("/admin/agents", agentToSubmit);
    return response.data.id;
  };

  const fetchAgent = async (
    agentId: string,
  ): Promise<IAgentBlueprint | null> => {
    const response = await apiClient.get(`/admin/agents/${agentId}`);
    return convertAgentBlueprintToFrontend(response.data);
  };

  const fetchMenu = async (agentId: string): Promise<Menu | null> => {
    const response = await apiClient.get(`/admin/agents/${agentId}/raw-menu`);
    return convertMenuToFrontend(response.data);
  };

  const updateMenu = async (agentId: string, menu: Menu) => {
    const response = await apiClient.post(
      `/admin/agents/${agentId}/raw-menu`,
      menu,
    );
    return convertMenuToFrontend(response.data);
  };

  const deployAgent = async (agent: IAgentBlueprint) => {
    if (!agent.id) {
      throw new Error("Agent ID is required");
    }

    let shouldCreate;
    try {
      const checkProd = await apiClient.get(`/admin/agents/${agent.id}`);
      shouldCreate = !checkProd.status;
    } catch {
      shouldCreate = true;
    }
    if (shouldCreate) {
      const agentBlueprintToDeploy = convertAgentBlueprintToBackend(agent);
      if (!agentBlueprintToDeploy) {
        throw new Error("Failed to convert agent blueprint to backend");
      }
      agentBlueprintToDeploy.slug = undefined;
      agentBlueprintToDeploy.hoursLoman = undefined;
      agentBlueprintToDeploy.faqs = undefined;
      agentBlueprintToDeploy.productId = undefined;
      agentBlueprintToDeploy.subscriptionItemId = undefined;
      agentBlueprintToDeploy.createdAt = undefined;
      agentBlueprintToDeploy.updatedAt = undefined;
      agentBlueprintToDeploy.deletedAt = undefined;
      agentBlueprintToDeploy.pausedUntil = undefined;
      agentBlueprintToDeploy.allowAnon = undefined;
      agentBlueprintToDeploy.panic = undefined;
      agentBlueprintToDeploy.reservationExternalId = undefined;
      await apiClient.post("/admin/agents", agentBlueprintToDeploy);
    } else {
      const agentToDeploy = convertAgentBlueprintToBackend(agent);
      if (!agentToDeploy) {
        throw new Error("Failed to convert agent blueprint to backend");
      }
      await apiClient.patch(`/admin/agents/${agent.id}`, agentToDeploy);
    }
  };

  const rescrapeMenu = async (agentId: string): Promise<void> => {
    await menuClient.post("/scrape", { agentId });
  };

  const lookupNearbyAreaCode = async (
    agentId: string | null,
  ): Promise<PhoneNumberLookupResponse> => {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }

    const response = await apiClient.get<PhoneNumberLookupResponse>(
      `/admin/agents/${agentId}/phoneNumber/lookup`,
    );
    return response.data;
  };

  const requestPhoneNumber = async (
    agentId: string | null,
    data: PhoneNumberPurchaseRequest,
  ) => {
    if (!agentId) {
      throw new Error("Agent ID is required");
    }
    const response = await apiClient.post<PhoneNumberPurchaseResponse>(
      `/admin/agents/${agentId}/phoneNumber`,
      data,
    );
    return response.data.phoneNumber;
    // const random4Digits = Math.floor(1000 + Math.random() * 9000).toString();
    // return `+1781229${random4Digits}`;
  };

  // TODO: is there such a thing as unpublished agents? that live only locally
  const fetchAdminAgents = async () => {
    const response = await apiClient.get("/admin/agents");
    const agents = response.data;
    return agents.sort((a: IAgent, b: IAgent) => a.name.localeCompare(b.name));
  };

  const fetchOrganizations = async (): Promise<IOrganization[]> => {
    const response = await apiClient.get("/admin/accounts");
    return response.data;
  };

  const createOrganization = async (name: string) => {
    const response = await apiClient.post("/admin/accounts", { name });
    return response.data;
  };

  const cleanMenu = async (
    agentId: string,
    onProgress?: (progress: number) => void,
  ) => {
    const menuBaseUrl = menuClient.defaults.baseURL!;
    const es = new EventSource(
      `${menuBaseUrl}/clean/progress?agentId=${encodeURIComponent(agentId)}`,
    );

    await new Promise<void>((resolve, reject) => {
      es.onopen = async () => {
        try {
          await menuClient.post("/clean", { agentId });
        } catch (error) {
          es.close();
          reject({
            title: "Failed to start menu cleaning",
            message:
              "An error occurred while starting the menu cleaning process.",
            errorDetails: getErrorMessage(error),
          });
        }
      };

      es.onmessage = (event) => {
        try {
          const { progress } = JSON.parse(event.data) as Record<
            string,
            unknown
          >;
          if (typeof progress !== "number") return;
          onProgress?.(progress);
          if (progress >= 100) {
            es.close();
            resolve();
          }
        } catch {
          return;
        }
      };

      es.onerror = () => {
        es.close();
        reject({
          title: "Menu cleaning failed",
          message: "An error occurred during menu cleaning.",
          errorDetails: "SSE connection error",
        });
      };
    });
  };

  const previewMenu = async (
    agentId: string,
    menuConfig: IMenuModuleConfig,
  ) => {
    const menuConfigToSubmit = convertMenuConfigToBackend(menuConfig);
    const response = await menuClient.post("/transform/preview", {
      agentId,
      menuConfig: menuConfigToSubmit,
    });
    return convertMenuToFrontend(response.data.menu);
  };

  type ITestCallResponse = {
    callId: string;
    accessToken: string;
  };
  const startTestCall = async (agentId: string): Promise<ITestCallResponse> => {
    const response = await apiClient.post(`/admin/agents/${agentId}/calls`);
    return response.data as ITestCallResponse;
  };

  // admin panic/recover
  const panicAgent = async (agentId: string) => {
    await apiClient.patch(`/admin/agents/${agentId}`, {
      panic: true,
    });
  };
  const recoverAgent = async (agentId: string) => {
    await apiClient.patch(`/admin/agents/${agentId}`, {
      panic: false,
    });
  };
  const panicAllAgents = async () => {
    await apiClient.post("/admin/panic");
  };
  const recoverAllAgents = async () => {
    await apiClient.post("/admin/recover");
  };

  const fetchActiveScrapingStates = async (): Promise<string[]> => {
    const response = await menuClient.get("/scrape");
    return response.data;
  };

  const fetchStripeCustomers = async (
    accountId: string,
  ): Promise<StripeCustomerResponse[]> => {
    const response = await apiClient.get(
      `/admin/accounts/${accountId}/stripe/customers`,
    );
    return response.data;
  };

  const createStripeCustomer = async (
    accountId: string,
    postStripeCustomerPost: StripeCustomerPost,
  ): Promise<StripeCustomerResponse> => {
    const response = await apiClient.post(
      `/admin/accounts/${accountId}/stripe/customers`,
      postStripeCustomerPost,
    );
    return response.data;
  };

  const fetchCustomerPaymentMethods = async (
    customerId: string,
  ): Promise<StripePaymentMethod[]> => {
    const response = await apiClient.get(
      `/admin/stripe/customers/${customerId}/payment-methods`,
    );
    return response.data;
  };

  const goLiveAgent = async (
    agentId: string,
    minimumPrice: number,
    pricePerMinute: number,
    pricePerCall: number,
    customerId: string | null,
    paymentMethodId: string,
    isAnnual: boolean,
  ): Promise<{ startedAt: string; subscriptionUrl: string }> => {
    const response = await apiClient.post(`/admin/agents/${agentId}/go-live`, {
      minimumPrice,
      pricePerMinute,
      pricePerCall,
      customerId,
      paymentMethodId,
      isAnnual,
    });
    return response.data;
  };

  const updateAgentPricing = async (
    agentId: string,
    minimumPrice: number,
    pricePerMinute: number,
    pricePerCall: number,
  ): Promise<void> => {
    await apiClient.patch(`/admin/agents/${agentId}/pricing`, {
      minimumPrice,
      pricePerMinute,
      pricePerCall,
    });
  };

  const markAgentActive = async (
    agentId: string,
  ): Promise<{ startedAt: string }> => {
    const response = await apiClient.post(`/admin/agents/${agentId}/start`);
    return response.data;
  };

  const billAccountOnboardingFee = async (
    accountId: string,
    amountInCents: number,
    achOnly: boolean = false,
  ): Promise<string> => {
    if (!accountId) {
      throw new Error("Account ID is required");
    }
    if (!amountInCents) {
      throw new Error("Amount in cents is required");
    }
    if (amountInCents <= 0) {
      throw new Error("Amount in cents must be greater than 0");
    }
    const response = await apiClient.post(
      `/admin/accounts/${accountId}/stripe/onboard`,
      {
        amount: amountInCents,
        achOnly,
      },
    );
    return response.data.url;
  };

  const deleteAgent = async (
    agentId: string,
    reason: string,
    deletedAt?: Date,
    nonActivation?: boolean,
  ) => {
    const params = new URLSearchParams();
    params.append("reason", reason);
    if (deletedAt) {
      params.append("deletedAt", deletedAt.toISOString());
    }
    if (nonActivation === true) {
      params.append("nonActivation", "true");
    }
    const response = await apiClient.delete(
      `/admin/agents/${agentId}?${params.toString()}`,
    );
    return response.data;
  };

  const impersonateUser = async (userId: string) => {
    const response = await apiClient.get(`/admin/users/${userId}/impersonate`);
    return response.data.url;
  };

  const snapshotMenu = async (agentId: string) => {
    const response = await menuClient.post(`/migrate/snapshot`, { agentId });
    return response.data;
  };

  const migrateMenu = async (
    agentId: string,
  ): Promise<IBackendMenuMigrationResponse> => {
    const response = await menuClient.post(`/migrate`, { agentId });
    return response.data;
  };

  const fetchSpotOnMenus = async (agentId: string): Promise<ISpotOnMenu[]> => {
    const response = await menuClient.post("/scrape/menus", { agentId });
    return response.data.menus;
  };

  const fetchToastConfig = async (agentId: string): Promise<IToastConfig> => {
    const response = await menuClient.get(
      `/scrape/toast-config?agentId=${agentId}`,
    );
    return response.data;
  };

  const fetchAgentOverviewMetrics = async (
    agentId: string,
    range: DateRange,
  ): Promise<AgentOverviewMetricsResponse> => {
    const response = await apiClient.get(`/admin/agents/${agentId}/analytics`, {
      params: range,
    });
    return response.data;
  };

  const importFromHubSpot = async (hsUrl: string) => {
    const response = await apiClient.get<{
      id: string;
      dealProperties: Record<string, string>;
    }>(`/admin/agents/import?hsUrl=${encodeURIComponent(hsUrl)}`);
    return response.data;
  };

  const fetchStreamChurn = async (query: {
    email?: string;
    accountId: string;
  }): Promise<{ redirectUrl: string }> => {
    const response = await apiClient.get("/connect/stream/churn", {
      params: {
        ...query,
        userId: "",
      },
    });
    return response.data;
  };

  const createAgentFromImport = async (
    agent: IAgentBlueprint,
    pricing: {
      customerId: string;
      minimumPrice: number;
      pricePerMinute?: number;
      pricePerCall?: number;
      setupFee?: number;
      achOnly?: boolean;
      isAnnual?: boolean;
    },
  ) => {
    const agentToSubmit = convertAgentBlueprintToBackend(agent);
    if (!agentToSubmit) {
      throw new Error("Failed to convert agent blueprint to backend");
    }
    agentToSubmit.phoneNumber = undefined;
    agentToSubmit.slug = undefined;
    agentToSubmit.hoursLoman = undefined;
    agentToSubmit.faqs = undefined;
    agentToSubmit.productId = undefined;
    agentToSubmit.subscriptionItemId = undefined;
    agentToSubmit.createdAt = undefined;
    agentToSubmit.updatedAt = undefined;
    agentToSubmit.deletedAt = undefined;
    agentToSubmit.pausedUntil = undefined;
    agentToSubmit.allowAnon = undefined;
    agentToSubmit.panic = undefined;

    const { data } = await apiClient.post("/admin/agents/import", {
      agent: agentToSubmit,
      pricing,
    });
    return data.agent.id;
  };

  interface FetchAgentCallsParams {
    cursor?: string | null;
    pageSize?: number;
    searchQuery?: string;
    hasOrder?: boolean;
    hasFeedback?: boolean;
    userSentiment?: string;
    userFrustration?: boolean;
    taskCompletion?: boolean;
    submissionType?: string;
    startDate?: string;
    endDate?: string;
  }
  const fetchAgentCalls = async (
    agentId: string,
    params: FetchAgentCallsParams = {},
  ): Promise<{ calls: AgentCall[]; nextCursor: string | null }> => {
    const searchParams = new URLSearchParams();
    searchParams.append("pageSize", String(params.pageSize ?? 20));
    if (params.cursor) {
      searchParams.append("cursor", params.cursor);
    }
    if (params.searchQuery) {
      searchParams.append("searchQuery", params.searchQuery);
    }
    if (params.hasOrder !== undefined) {
      searchParams.append("hasOrder", params.hasOrder.toString());
    }
    if (params.hasFeedback !== undefined) {
      searchParams.append("hasFeedback", params.hasFeedback.toString());
    }
    if (params.userSentiment) {
      searchParams.append("userSentiment", params.userSentiment);
    }
    if (params.userFrustration !== undefined) {
      searchParams.append("userFrustration", params.userFrustration.toString());
    }
    if (params.taskCompletion !== undefined) {
      searchParams.append("taskCompletion", params.taskCompletion.toString());
    }
    if (params.submissionType) {
      searchParams.append("submissionType", params.submissionType);
    }
    if (params.startDate) {
      searchParams.append("startDate", params.startDate);
    }
    if (params.endDate) {
      searchParams.append("endDate", params.endDate);
    }
    const response = await apiClient.get(
      `/admin/agents/${agentId}/calls?${searchParams.toString()}`,
    );
    return response.data;
  };

  interface AuditTrailItem {
    auditId: number;
    auditTimestamp: string;
    actorEmail: string | null;
    diff: Record<
      string,
      {
        old: unknown;
        new: unknown;
      }
    >;
  }
  const fetchAuditTrail = async (
    agentId: string,
    cursor?: string | null,
  ): Promise<{ items: AuditTrailItem[]; nextCursor: string | null }> => {
    const params = new URLSearchParams();
    if (cursor) {
      params.append("cursor", cursor);
    }
    const response = await apiClient.get(
      `/admin/agents/${agentId}/audit?${params.toString()}`,
    );
    return response.data;
  };

  interface FirstDeliveryMerchantResponse {
    type: string;
    data: {
      id: string;
      location: {
        address: string;
        coordinates: {
          type: string;
          coordinates: number[];
        };
      };
    };
  }
  const createFirstDeliveryMerchant = async (
    agentId: string,
    data: {
      name: string;
      address: string;
      email: string;
      phone: string;
      territory_id: string;
    },
  ): Promise<FirstDeliveryMerchantResponse> => {
    const response = await apiClient.post<FirstDeliveryMerchantResponse>(
      `/admin/agents/${agentId}/merchant`,
      data,
    );
    return response.data;
  };

  const fetchEvalsPage = async (
    agentId: string,
    options: FetchEvalsPageOptions = {},
  ): Promise<{
    evals: EvalWithRunsResponse[];
    nextCursor: string | null;
    totalPages: number;
    stats: EvalsStatsResponse;
  }> => {
    const params = new URLSearchParams();
    if (options.cursor) params.append("cursor", options.cursor);
    if (options.pageSize != null) {
      params.append("pageSize", String(options.pageSize));
    }
    if (options.runsPageSize != null) {
      params.append("runsPageSize", String(options.runsPageSize));
    }
    if (options.filter != null) {
      params.append("filter", options.filter);
    }
    const response = await apiClient.get(
      `/admin/agents/${agentId}/evals?${params.toString()}`,
      { signal: options.signal },
    );
    return response.data;
  };

  const fetchEvalRunsPage = async (
    agentId: string,
    evalId: string,
    options: FetchEvalRunsPageOptions = {},
  ): Promise<PaginatedEvalRunsResponse> => {
    const params = new URLSearchParams();
    if (options.cursor) params.append("cursor", options.cursor);
    if (options.pageSize != null) {
      params.append("pageSize", String(options.pageSize));
    }
    const response = await apiClient.get(
      `/admin/agents/${agentId}/evals/${evalId}/runs?${params.toString()}`,
      { signal: options.signal },
    );
    return response.data;
  };

  const createEval = async (
    agentId: string,
    data: CreateAgentEvalRequest,
  ): Promise<AgentEvalResponse> => {
    const response = await apiClient.post<AgentEvalResponse>(
      `/admin/agents/${agentId}/evals`,
      data,
    );
    return response.data;
  };

  const updateEval = async (
    agentId: string,
    evalId: string,
    data: UpdateAgentEvalRequest,
  ): Promise<AgentEvalResponse> => {
    const response = await apiClient.patch<AgentEvalResponse>(
      `/admin/agents/${agentId}/evals/${evalId}`,
      data,
    );
    return response.data;
  };

  const startEvalRun = async (
    agentId: string,
    evalId: string,
  ): Promise<EvalRunResponse | null> => {
    try {
      const response = await apiClient.post<EvalRunResponse | null>(
        `/admin/agents/${agentId}/evals/${evalId}/run`,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        return null;
      }
      throw error;
    }
  };

  const startAllEvalRuns = async (
    agentId: string,
  ): Promise<{ enqueuedRuns: number; failedRuns: number }> => {
    const response = await apiClient.post<{
      enqueuedRuns: number;
      failedRuns: number;
    }>(`/admin/agents/${agentId}/evals/run`);
    return response.data;
  };

  const stopEvalRunById = async (
    agentId: string,
    evalRunId: string,
  ): Promise<EvalRunResponse> => {
    const response = await apiClient.post<EvalRunResponse>(
      `/admin/agents/${agentId}/evals/runs/${evalRunId}/stop`,
    );
    return response.data;
  };

  const deleteEvalRunById = async (
    agentId: string,
    evalRunId: string,
  ): Promise<void> => {
    await apiClient.delete(`/admin/agents/${agentId}/evals/runs/${evalRunId}`);
  };

  const deleteEvalById = async (
    agentId: string,
    evalId: string,
  ): Promise<void> => {
    await apiClient.delete(`/admin/agents/${agentId}/evals/${evalId}`);
  };

  return {
    createAgent,
    fetchAgent,
    fetchMenu,
    updateMenu,
    deployAgent,
    rescrapeMenu,
    lookupNearbyAreaCode,
    requestPhoneNumber,
    fetchAdminAgents,
    fetchOrganizations,
    createOrganization,
    cleanMenu,
    previewMenu,
    startTestCall,
    panicAgent,
    recoverAgent,
    panicAllAgents,
    recoverAllAgents,
    fetchActiveScrapingStates,
    fetchStripeCustomers,
    createStripeCustomer,
    fetchCustomerPaymentMethods,
    goLiveAgent,
    updateAgentPricing,
    markAgentActive,
    billAccountOnboardingFee,
    impersonateUser,
    snapshotMenu,
    migrateMenu,
    deleteAgent,
    fetchSpotOnMenus,
    fetchToastConfig,
    fetchAgentOverviewMetrics,
    importFromHubSpot,
    fetchStreamChurn,
    createAgentFromImport,
    fetchAgentCalls,
    fetchAuditTrail,
    createFirstDeliveryMerchant,
    fetchEvalsPage,
    fetchEvalRunsPage,
    stopEvalRunById,
    deleteEvalRunById,
    deleteEvalById,
    createEval,
    updateEval,
    startEvalRun,
    startAllEvalRuns,
  };
}
